import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  Check, 
  Download, 
  Heart, 
  Mail, 
  Phone, 
  MapPin, 
  Building2, 
  Briefcase, 
  Star, 
  Globe, 
  Linkedin, 
  Twitter, 
  Github, 
  MoreHorizontal, 
  Eye, 
  UserPlus, 
  MessageCircle, 
  TrendingUp, 
  Zap, 
  Shield, 
  AlertCircle, 
  CheckCircle2,
  Users,
  Target,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';

interface SearchContact {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  jobTitle: string;
  company: {
    name: string;
    domain: string;
    industry: string;
    size: string;
    location: string;
    logo?: string;
  };
  location: {
    city: string;
    state: string;
    country: string;
  };
  socialProfiles: {
    linkedin?: string;
    twitter?: string;
    github?: string;
  };
  leadScore: number;
  intentScore: number;
  emailVerificationStatus: 'verified' | 'unverified' | 'invalid' | 'pending';
  lastActivity?: string;
  technologies?: string[];
  dataSourceAttribution: string[];
  freshnessScore: number;
  qualityScore: number;
  avatar?: string;
}

interface SearchResults {
  contacts: SearchContact[];
  totalCount: number;
  page: number;
  hasMore: boolean;
  aggregations: {
    industries: Array<{ name: string; count: number }>;
    companies: Array<{ name: string; count: number }>;
    locations: Array<{ name: string; count: number }>;
    jobTitles: Array<{ name: string; count: number }>;
  };
  searchTime: number;
}

interface SearchResultsSectionProps {
  results: SearchResults | null;
  isSearching: boolean;
  selectedContacts: Set<string>;
  onContactSelection: (contactId: string, selected: boolean) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBulkExport: (format: 'csv' | 'json') => void;
  sortBy: 'relevance' | 'leadScore' | 'lastActivity' | 'alphabetical';
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: string) => void;
}

export const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
  results,
  isSearching,
  selectedContacts,
  onContactSelection,
  onSelectAll,
  onClearSelection,
  onBulkExport,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  const [contactDetailsOpen, setContactDetailsOpen] = useState<string | null>(null);

  if (isSearching && !results) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
              Searching...
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              Finding the perfect prospects for you
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!results) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <Users className="w-16 h-16 mx-auto text-neutral-400 mb-4" />
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              Start Your Search
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400">
              Enter search terms or apply filters to find prospects
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const allSelected = results.contacts.length > 0 && results.contacts.every(c => selectedContacts.has(c.id));
  const someSelected = selectedContacts.size > 0;

  const getEmailStatusIcon = (status: SearchContact['emailVerificationStatus']) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'unverified':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'invalid':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <div className="w-4 h-4 border-2 border-neutral-300 border-t-primary-500 rounded-full animate-spin" />;
      default:
        return null;
    }
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (score >= 60) return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  const ContactCard: React.FC<{ contact: SearchContact; index: number }> = ({ contact, index }) => {
    const isSelected = selectedContacts.has(contact.id);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={cn(
          "border rounded-lg transition-all duration-200 hover:shadow-md",
          isSelected 
            ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20" 
            : "border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="p-6">
          {/* Header with selection and actions */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onContactSelection(contact.id, e.target.checked)}
                  className="mr-3 rounded text-primary-600 focus:ring-primary-500"
                />
              </label>
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                {contact.avatar ? (
                  <img src={contact.avatar} alt={contact.fullName} className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  `${contact.firstName[0]}${contact.lastName[0]}`
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {contact.fullName}
                  </h3>
                  <div className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    getLeadScoreColor(contact.leadScore)
                  )}>
                    {contact.leadScore}% Lead Score
                  </div>
                </div>
                <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                  {contact.jobTitle}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <Building2 className="w-4 h-4 text-neutral-500" />
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {contact.company.name}
                  </span>
                  <span className="text-neutral-400">•</span>
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {contact.company.industry}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    className="flex items-center space-x-1"
                  >
                    <Button size="sm" variant="outline">
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline">
                      <Phone className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline">
                      <Heart className="w-4 h-4" />
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setContactDetailsOpen(contactDetailsOpen === contact.id ? null : contact.id)}
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Contact info row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-neutral-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
                {contact.email}
              </span>
              {getEmailStatusIcon(contact.emailVerificationStatus)}
            </div>
            {contact.phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  {contact.phone}
                </span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-neutral-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                {contact.location.city}, {contact.location.state}
              </span>
            </div>
          </div>

          {/* Metrics row */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-lg font-bold text-primary-600">
                {contact.intentScore}%
              </div>
              <div className="text-xs text-neutral-500">Intent Score</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {contact.qualityScore}%
              </div>
              <div className="text-xs text-neutral-500">Quality</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                {contact.freshnessScore}%
              </div>
              <div className="text-xs text-neutral-500">Freshness</div>
            </div>
            <div className="text-center">
              {contact.lastActivity && (
                <>
                  <div className="text-lg font-bold text-blue-600">
                    {Math.floor((Date.now() - new Date(contact.lastActivity).getTime()) / (1000 * 60 * 60 * 24))}d
                  </div>
                  <div className="text-xs text-neutral-500">Last Active</div>
                </>
              )}
            </div>
          </div>

          {/* Technologies */}
          {contact.technologies && contact.technologies.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {contact.technologies.slice(0, 5).map((tech, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-xs rounded-full"
                  >
                    {tech}
                  </span>
                ))}
                {contact.technologies.length > 5 && (
                  <span className="px-2 py-1 bg-neutral-100 dark:bg-neutral-700 text-neutral-500 text-xs rounded-full">
                    +{contact.technologies.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Social profiles */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {contact.socialProfiles.linkedin && (
                <a
                  href={contact.socialProfiles.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-blue-600 hover:text-blue-700"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {contact.socialProfiles.twitter && (
                <a
                  href={contact.socialProfiles.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-blue-400 hover:text-blue-500"
                >
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {contact.socialProfiles.github && (
                <a
                  href={contact.socialProfiles.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-neutral-600 hover:text-neutral-700 dark:text-neutral-400"
                >
                  <Github className="w-4 h-4" />
                </a>
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-neutral-500">
              <span>Sources:</span>
              {contact.dataSourceAttribution.map((source, i) => (
                <span key={i} className="px-1 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded">
                  {source}
                </span>
              ))}
            </div>
          </div>

          {/* Expanded details */}
          <AnimatePresence>
            {contactDetailsOpen === contact.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">Company Details</h4>
                    <div className="space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
                      <div>Size: {contact.company.size}</div>
                      <div>Industry: {contact.company.industry}</div>
                      <div>Location: {contact.company.location}</div>
                      {contact.company.domain && (
                        <div className="flex items-center space-x-1">
                          <Globe className="w-3 h-3" />
                          <a href={`https://${contact.company.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                            {contact.company.domain}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">AI Insights</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">Likelihood to Convert</span>
                        <div className="flex items-center space-x-1">
                          <div className="w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                              style={{ width: `${contact.leadScore}%` }}
                            />
                          </div>
                          <span className="text-neutral-700 dark:text-neutral-300 font-medium">{contact.leadScore}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-600 dark:text-neutral-400">Purchase Intent</span>
                        <div className="flex items-center space-x-1">
                          <div className="w-20 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                              style={{ width: `${contact.intentScore}%` }}
                            />
                          </div>
                          <span className="text-neutral-700 dark:text-neutral-300 font-medium">{contact.intentScore}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                  {results.totalCount.toLocaleString()} Results
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Found in {results.searchTime}ms
                </p>
              </div>
              
              {someSelected && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                    {selectedContacts.size} selected
                  </span>
                  <Button size="sm" variant="outline" onClick={onClearSelection}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => onBulkExport('csv')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onBulkExport('json')}>
                    Export JSON
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={allSelected ? onClearSelection : onSelectAll}
                  className="rounded text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Select all
                </span>
              </label>
              
              <div className="flex items-center space-x-1 ml-4">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">Sort by:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [sort, order] = e.target.value.split('-');
                    onSortChange(sort, order);
                  }}
                  className="border border-neutral-300 dark:border-neutral-600 rounded px-2 py-1 text-sm bg-white dark:bg-neutral-800"
                >
                  <option value="relevance-desc">Relevance</option>
                  <option value="leadScore-desc">Lead Score (High to Low)</option>
                  <option value="leadScore-asc">Lead Score (Low to High)</option>
                  <option value="lastActivity-desc">Recently Active</option>
                  <option value="alphabetical-asc">Name (A-Z)</option>
                  <option value="alphabetical-desc">Name (Z-A)</option>
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Results */}
      <div className="space-y-4">
        {results.contacts.map((contact, index) => (
          <ContactCard key={contact.id} contact={contact} index={index} />
        ))}
      </div>

      {/* Load More */}
      {results.hasMore && (
        <div className="text-center py-6">
          <Button size="lg" variant="outline">
            Load More Results
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchResultsSection; 